import { IPDefaultButton, IPPrimaryButton } from 'baseComponents';
import { IPPanel, IPPanelType } from 'baseComponents/Panel/IPPanel';
import { IPStack } from 'baseComponents/Stack/IPStack';
import GenericModal from 'components/Dialogs/GenericModal';
import { AddFieldDetails } from 'components/KPI/AddFieldDetails';
import { EditFieldDetails } from 'components/KPI/EditFieldDetails';
import { SaveCallback, SaveDisabledCallback } from 'components/KPI/fieldPanelTypes';
import { onRenderPanelHeaderGlobal } from 'globalFunctions';
import { globalLayerRightSideBarProps, globalStackStylesPanelFooter, globalStackTokensGapSmall } from 'globalStyles';
import KPI from 'models/kpi/kpi';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

//
// One shell for BOTH adding and editing a field / KPI — side panel, centered modal, or inline block.
// Content switches by mode: AddFieldDetails (create-with-reuse) or EditFieldDetails (edit).
//
interface IFieldPanelProps {
  mode: 'add' | 'edit';
  isOpen: boolean;
  onDismiss: () => void;
  isLoading: boolean;
  kpis: KPI[];
  formKpiIds?: number[];
  renderAsPanel?: boolean;
  updatePreviewKPI?: (kpi: KPI) => void;
  onAddNewKPI?: (item: KPI) => void | Promise<void | boolean>;
  defaultIsField?: boolean;
  candidates?: KPI[];
  onReuse?: (kpi: KPI) => void;
  onCreateNew?: (name: string) => void;
  defaultName?: string;
  hideReuse?: boolean;
  kpiToEdit?: KPI;
  onEditKPI?: (item: KPI) => void | Promise<void | boolean>;
  taskTypeContext?: number;
  onlyIntegrationProps?: boolean;
  previewDismissed?: boolean;
  onTogglePreview?: () => void;
  previewToggleId?: string;
}

const noopPreview = () => undefined;

const FieldPanel = (props: IFieldPanelProps) => {
  const { t } = useTranslation(['translation', 'kpis', 'forms']);
  const renderAsPanel = props.renderAsPanel ?? true;
  const updatePreviewKPI = props.updatePreviewKPI ?? noopPreview;
  const [saveCallback, setSaveCallback] = useState<SaveCallback | undefined>(undefined);
  const [saveDisabledCallback, setSaveDisabledCallback] = useState<SaveDisabledCallback | undefined>(undefined);
  const [selectedPivotKey, setSelectedPivotKey] = useState<string>('details');
  const [isField, setIsField] = useState<boolean>(props.defaultIsField ?? false);
  const [isCommitting, setIsCommitting] = useState<boolean>(false);

  useEffect(() => {
    if (props.isOpen) {
      setSelectedPivotKey('details');
      setIsField(props.defaultIsField ?? false);
      setIsCommitting(false);
    }
  }, [props.defaultIsField, props.isOpen]);

  const onSetSaveDisabledCallback = (cb: SaveDisabledCallback) => setSaveDisabledCallback(() => cb);
  const onSetSaveCallback = (cb: SaveCallback) => setSaveCallback(() => cb);

  const showFooter = props.mode === 'add' || selectedPivotKey === 'details';
  const saveDisabled = isCommitting || (saveDisabledCallback ? saveDisabledCallback() : true);

  const onSave = async () => {
    if (!saveCallback) {
      return;
    }

    try {
      setIsCommitting(true);
      const accepted = await saveCallback();
      if (accepted !== false) {
        props.onDismiss();
      }
    } finally {
      setIsCommitting(false);
    }
  };

  const detailsContent =
    props.mode === 'add' ? (
      <AddFieldDetails
        isOpen={props.isOpen}
        onDismiss={props.onDismiss}
        isLoading={props.isLoading}
        kpis={props.kpis}
        formKpiIds={props.formKpiIds}
        updatePreviewKPI={updatePreviewKPI}
        onAddNewKPI={props.onAddNewKPI ?? (() => undefined)}
        defaultIsField={props.defaultIsField}
        onIsFieldChange={setIsField}
        candidates={props.candidates}
        onReuse={props.onReuse}
        onCreateNew={props.onCreateNew}
        defaultName={props.defaultName}
        hideReuse={props.hideReuse}
        onSaveCallback={onSetSaveCallback}
        onSaveDisabledCallback={onSetSaveDisabledCallback}
        previewDismissed={props.previewDismissed}
        onTogglePreview={props.onTogglePreview}
        previewToggleId={props.previewToggleId}
        compactLayout={!renderAsPanel}
      />
    ) : (
      props.kpiToEdit && (
        <EditFieldDetails
          isOpen={props.isOpen}
          onDismiss={props.onDismiss}
          isLoading={props.isLoading}
          kpis={props.kpis}
          formKpiIds={props.formKpiIds}
          kpiToEdit={props.kpiToEdit}
          onEditKPI={props.onEditKPI ?? (() => undefined)}
          updatePreviewKPI={updatePreviewKPI}
          taskTypeContext={props.taskTypeContext}
          onlyIntegrationProps={props.onlyIntegrationProps}
          onChangeSelectedPivot={setSelectedPivotKey}
          onSaveCallback={onSetSaveCallback}
          onSaveDisabledCallback={onSetSaveDisabledCallback}
          compactLayout={!renderAsPanel}
          previewDismissed={props.previewDismissed}
          onTogglePreview={props.onTogglePreview}
          previewToggleId={props.previewToggleId}
        />
      )
    );

  const onRenderFooterContent = () => (
    <IPStack horizontal wrap styles={globalStackStylesPanelFooter} tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <IPPrimaryButton
          disabled={saveDisabled}
          onClick={onSave}
          text={props.mode === 'edit' ? t('forms:Versioning.Apply') : t('translation:General.Button.Save')}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPDefaultButton onClick={props.onDismiss} text={t('translation:General.Button.Cancel')} />
      </IPStack.Item>
    </IPStack>
  );

  if (renderAsPanel) {
    return (
      <IPPanel
        headerText={
          props.mode === 'add' ? (isField ? t('kpis:Panels.Add.FieldTitle') : t('kpis:Panels.Add.Title')) : undefined
        }
        onRenderHeader={
          props.mode === 'edit' && props.kpiToEdit
            ? onRenderPanelHeaderGlobal(
                t(props.kpiToEdit.isField ? 'kpis:Panels.Edit.FieldTitle' : 'kpis:Panels.Edit.Title'),
                props.kpiToEdit.kpiId,
                false,
              )
            : undefined
        }
        type={IPPanelType.medium}
        isOpen={props.isOpen}
        isBlocking={true}
        isFooterAtBottom={true}
        onRenderFooterContent={showFooter ? onRenderFooterContent : undefined}
        hasCloseButton={false}
        layerProps={globalLayerRightSideBarProps}
        focusTrapZoneProps={{ forceFocusInsideTrap: false }}
      >
        {detailsContent}
      </IPPanel>
    );
  }

  const modalTitle =
    props.mode === 'add'
      ? isField
        ? t('kpis:Panels.Add.FieldTitle')
        : t('kpis:Panels.Add.Title')
      : props.kpiToEdit?.isField
      ? t('kpis:Panels.Edit.FieldTitle')
      : t('kpis:Panels.Edit.Title');
  const modalWidth = props.mode === 'add' ? 660 : 720;

  return (
    <GenericModal
      title={modalTitle}
      isOpen={props.isOpen}
      onClose={props.onDismiss}
      saveDisabled={showFooter ? saveDisabled : undefined}
      onSave={showFooter ? onSave : undefined}
      isActionPending={isCommitting}
      keepOpenAfterSave
      growContent={props.mode === 'edit'}
      width={modalWidth}
      maxWidth={`min(92vw, ${modalWidth}px)`}
      minWidth={props.mode === 'add' ? 520 : 560}
      height={props.mode === 'edit' ? 'min(90vh, 720px)' : undefined}
      maxHeight={'min(90vh, 720px)'}
      titleSeparator
    >
      {detailsContent}
    </GenericModal>
  );
};

export default FieldPanel;
